import { z } from 'zod';
import { IsoDateTime, NonEmptyName, Pence, Uuid } from '../primitives.js';

/**
 * A bookable service. `durationMin` is chair time; `processingMin` is hands-off time
 * (e.g. colour developing) during which the chair can be reused, and `gapMin` is buffer
 * after. The availability engine uses all three.
 */
export const Service = z
  .object({
    id: Uuid,
    salonId: Uuid,
    name: NonEmptyName,
    category: z.string().max(80).nullable(),
    durationMin: z.int().min(5).max(600),
    processingMin: z.int().min(0).max(240).describe('hands-off time inside the booking'),
    gapMin: z.int().min(0).max(120).describe('buffer/clean-down after the booking'),
    priceFrom: Pence,
    requiresPatchTest: z.boolean(),
    patchTestValidDays: z.int().min(1).max(3650).nullable(),
    onlineBookable: z.boolean(),
    active: z.boolean(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'Service' });
export type Service = z.infer<typeof Service>;

export const CreateService = z
  .object({
    name: NonEmptyName,
    category: z.string().max(80).nullish(),
    durationMin: z.int().min(5).max(600),
    processingMin: z.int().min(0).max(240).default(0),
    gapMin: z.int().min(0).max(120).default(0),
    priceFrom: Pence,
    requiresPatchTest: z.boolean().default(false),
    patchTestValidDays: z.int().min(1).max(3650).nullish(),
    onlineBookable: z.boolean().default(true),
  })
  .meta({ id: 'CreateService' });
export type CreateService = z.infer<typeof CreateService>;

export const UpdateService = CreateService.partial()
  .extend({ active: z.boolean().optional() })
  .meta({ id: 'UpdateService' });
export type UpdateService = z.infer<typeof UpdateService>;
